/**
 * Browser storage backend.
 *
 * Wraps `localStorage` so a signed-in donor stays signed in across reloads.
 * Every access is guarded: private browsing, disabled site data and quota
 * errors all throw, and a storage failure must never take the app down — it
 * degrades to an in-memory session instead.
 */

import { setBackend, type StorageBackend } from './storage';

class WebStorage implements StorageBackend {
  private readonly fallback = new Map<string, string>();

  private get store(): Storage | null {
    try {
      // Touching localStorage is itself what throws in a blocked context.
      const probe = globalThis.localStorage;
      probe.getItem('__mfa_probe__');
      return probe;
    } catch {
      return null;
    }
  }

  async getItem(key: string): Promise<string | null> {
    const store = this.store;
    if (!store) return this.fallback.get(key) ?? null;
    try {
      return store.getItem(key);
    } catch {
      return this.fallback.get(key) ?? null;
    }
  }

  async setItem(key: string, value: string): Promise<void> {
    this.fallback.set(key, value);
    try {
      this.store?.setItem(key, value);
    } catch {
      // Quota or blocked storage: the in-memory copy still serves this session.
    }
  }

  async removeItem(key: string): Promise<void> {
    this.fallback.delete(key);
    try {
      this.store?.removeItem(key);
    } catch {
      // Nothing to do — the value is already gone from the fallback.
    }
  }
}

export function installWebStorage(): void {
  setBackend(new WebStorage());
}
